import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AdditionalServices } from '@/components/additional-services'
import { SIZES } from '@/shared/types/pet.types'
import { Button } from '@/shared/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/ui/dialog'
import { Input } from '@/shared/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select'
import { Textarea } from '@/shared/ui/textarea'
import { Pet, PetSize } from '@monorepo/functions/src/types/reservations'
import { AdditionalService } from '@monorepo/functions/src/types/services'

interface EditPetDialogProps {
    pet: Pet
    isOpen: boolean
    onClose: () => void
    onSave: (pet: Pet) => void
    hideObservations?: boolean
}

export function EditPetDialog({ pet, isOpen, onClose, onSave, hideObservations = false }: EditPetDialogProps) {
    const { t } = useTranslation(undefined, { keyPrefix: 'pet' })

    const [editedPet, setEditedPet] = useState<Pet>({ ...pet })

    useEffect(() => {
        const weight = editedPet.weight
        let size: PetSize = 'pequeño'
        if (weight <= 10) size = 'pequeño'
        else if (weight <= 20) size = 'mediano'
        else size = 'grande'
        setEditedPet(prev => ({ ...prev, size }))
    }, [editedPet.weight])

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        onSave(editedPet)
        onClose()
    }

    const handleServiceChange = (services: AdditionalService[]) => {
        setEditedPet(prev => ({ ...prev, additionalServices: services }))
    }

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className='max-h-[90vh] w-[95vw] max-w-[500px] p-0'>
                <form onSubmit={handleSubmit} className='flex h-full max-h-[90vh] flex-col'>
                    <DialogHeader className='shrink-0 border-b px-4 py-2'>
                        <DialogTitle className='text-base font-medium'>{t('editPet', 'Edit Pet')}</DialogTitle>
                    </DialogHeader>

                    <div className='flex-1 overflow-y-auto'>
                        <div className='space-y-4 px-4 py-3'>
                            <div>
                                <label className='text-sm font-medium'>{t('name', 'Name')}</label>
                                <Input
                                    value={editedPet.name}
                                    onChange={e => setEditedPet(prev => ({ ...prev, name: e.target.value }))}
                                />
                            </div>

                            <div>
                                <label className='text-sm font-medium'>{t('breed', 'Breed')}</label>
                                <Input
                                    value={editedPet.breed}
                                    onChange={e => setEditedPet(prev => ({ ...prev, breed: e.target.value }))}
                                />
                            </div>

                            <div>
                                <label className='text-sm font-medium'>{t('age', 'Age')}</label>
                                <Input
                                    value={editedPet.age || ''}
                                    onChange={e => setEditedPet(prev => ({ ...prev, age: e.target.value }))}
                                    placeholder={t('agePlaceholder', 'Ej: 2 años, 6 meses, 18 meses')}
                                />
                            </div>

                            <div className='grid grid-cols-2 gap-4'>
                                <div>
                                    <label className='text-sm font-medium'>{t('weight', 'Weight')} (kg)</label>
                                    <Input
                                        type='number'
                                        step='0.1'
                                        value={editedPet.weight}
                                        onChange={e =>
                                            setEditedPet(prev => ({ ...prev, weight: parseFloat(e.target.value) }))
                                        }
                                    />
                                </div>

                                <div>
                                    <label className='text-sm font-medium'>{t('size', 'Size')}</label>
                                    <Select
                                        value={editedPet.size}
                                        onValueChange={(value: PetSize) =>
                                            setEditedPet(prev => ({ ...prev, size: value }))
                                        }
                                    >
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {SIZES.map(size => (
                                                <SelectItem key={size} value={size}>
                                                    {t(
                                                        size === 'pequeño'
                                                            ? 'small'
                                                            : size === 'mediano'
                                                              ? 'medium'
                                                              : 'large',
                                                    )}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            <div>
                                <label className='text-sm font-medium'>{t('sex', 'Sex')}</label>
                                <Select
                                    value={editedPet.sex || 'M'}
                                    onValueChange={(value: 'M' | 'F') =>
                                        setEditedPet(prev => ({ ...prev, sex: value }))
                                    }
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value='M'>{t('male')}</SelectItem>
                                        <SelectItem value='F'>{t('female')}</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div>
                                <label className='text-sm font-medium'>{t('neutered', 'Neutered/Spayed')}</label>
                                <Select
                                    value={editedPet.isNeutered ? 'yes' : 'no'}
                                    onValueChange={(value: 'yes' | 'no') =>
                                        setEditedPet(prev => ({ ...prev, isNeutered: value === 'yes' }))
                                    }
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value='yes'>{t('yes')}</SelectItem>
                                        <SelectItem value='no'>{t('no')}</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div>
                                <label className='text-sm font-medium'>{t('personality', 'Personality')}</label>
                                <Textarea
                                    value={editedPet.personality || ''}
                                    onChange={e => setEditedPet(prev => ({ ...prev, personality: e.target.value }))}
                                    placeholder='Personalidad y hábitos de la mascota...'
                                />
                            </div>

                            {!hideObservations && (
                                <div>
                                    <label className='text-sm font-medium'>{t('observations', 'Observations')}</label>
                                    <Textarea
                                        value={editedPet.observations || ''}
                                        onChange={e =>
                                            setEditedPet(prev => ({ ...prev, observations: e.target.value }))
                                        }
                                        placeholder={t(
                                            'observationsPlaceholder',
                                            'Observaciones especiales sobre la mascota...',
                                        )}
                                    />
                                </div>
                            )}

                            <div className='pb-4'>
                                <AdditionalServices
                                    onServiceChange={handleServiceChange}
                                    petCount={1}
                                    initialServices={editedPet.additionalServices || []}
                                    hideDriverService
                                />
                            </div>
                        </div>
                    </div>

                    <div className='shrink-0 border-t p-4'>
                        <Button type='submit' className='w-full'>
                            {t('save')}
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    )
}
